/**
 * VendorPortalBaseService
 * Base service for Vendor Portal operations with X-Vendor-ID header support.
 * All vendor portal services extend this to automatically include the vendor context.
 */
/**
 * Abstract base service for vendor portal endpoints.
 * Handles X-Vendor-ID header injection for vendor-scoped requests.
 */
export class VendorPortalBaseService {
    constructor(http, config) {
        this.vendorId = null;
        this.http = http;
        this.servicePath = config.servicePath;
        this.basePath = config.basePath;
        this.endpointOverrides = config.endpoints ?? {};
    }
    /**
     * Set the vendor ID for all subsequent requests.
     * This ID will be sent as the X-Vendor-ID header.
     * @param vendorId - The vendor ID to use
     */
    setVendorId(vendorId) {
        this.vendorId = vendorId;
    }
    /**
     * Get the current vendor ID
     */
    getVendorId() {
        return this.vendorId;
    }
    /**
     * Get the endpoint path, checking for overrides
     */
    getEndpoint(key, defaultPath) {
        return this.endpointOverrides[key] ?? defaultPath;
    }
    /**
     * Build the full URL path for an endpoint
     */
    buildUrl(endpoint) {
        const parts = [];
        if (this.servicePath) {
            parts.push(this.servicePath);
        }
        parts.push(this.basePath);
        if (endpoint) {
            parts.push(endpoint);
        }
        const url = '/' + parts.join('/').replace(/\/+/g, '/').replace(/^\//, '');
        return url;
    }
    /**
     * Build URL with query parameters
     */
    buildUrlWithParams(endpoint, params) {
        const url = this.buildUrl(endpoint);
        if (!params) {
            return url;
        }
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                searchParams.append(key, String(value));
            }
        });
        const queryString = searchParams.toString();
        return queryString ? `${url}?${queryString}` : url;
    }
    /**
     * Make a GET request with vendor context
     */
    async get(endpoint) {
        const url = this.buildUrl(endpoint);
        this.ensureVendorHeader();
        return this.http.get(url);
    }
    /**
     * Make a POST request with vendor context
     */
    async post(endpoint, body) {
        const url = this.buildUrl(endpoint);
        this.ensureVendorHeader();
        return this.http.post(url, body);
    }
    /**
     * Make a PUT request with vendor context
     */
    async put(endpoint, body) {
        const url = this.buildUrl(endpoint);
        this.ensureVendorHeader();
        return this.http.put(url, body);
    }
    /**
     * Make a PATCH request with vendor context
     */
    async patch(endpoint, body) {
        const url = this.buildUrl(endpoint);
        this.ensureVendorHeader();
        return this.http.patch(url, body);
    }
    /**
     * Make a DELETE request with vendor context
     */
    async delete(endpoint) {
        const url = this.buildUrl(endpoint);
        this.ensureVendorHeader();
        return this.http.delete(url);
    }
    /**
     * Ensure the X-Vendor-ID header is set via interceptor
     */
    ensureVendorHeader() {
        if (this.vendorId !== null) {
            // Add interceptor to inject vendor ID header
            const interceptors = this.http.getInterceptors();
            interceptors.addRequestInterceptor((ctx) => {
                ctx.init.headers = {
                    ...ctx.init.headers,
                    'X-Vendor-ID': String(this.vendorId)
                };
                return ctx;
            });
        }
    }
}
//# sourceMappingURL=VendorPortalBaseService.js.map